// src/lib/resilience.ts
// Retry and circuit-breaker utilities for frontend API calls.

import logger from './logger';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------
const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_BACKOFF_BASE_MS = 500;
const DEFAULT_BACKOFF_MAX_MS = 10_000;

const CIRCUIT_FAILURE_THRESHOLD = 5;
const CIRCUIT_RECOVERY_TIMEOUT_MS = 60_000;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns true for errors that are safe to retry (network / 5xx). */
function isRetryable(error: unknown): boolean {
  // Network failure (no response at all)
  if (error instanceof TypeError && error.message === 'Failed to fetch') {
    return true;
  }

  // Axios-style errors with a response status
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (typeof status === 'number') {
    // Retry on 429 (rate-limit) and 5xx server errors
    return status === 429 || (status >= 500 && status < 600);
  }

  // Axios network errors (no response object)
  const code = (error as { code?: string })?.code;
  if (code === 'ECONNABORTED' || code === 'ERR_NETWORK') {
    return true;
  }

  return false;
}

/** Exponential backoff with jitter. */
function backoffMs(
  attempt: number,
  baseMs: number = DEFAULT_BACKOFF_BASE_MS,
  maxMs: number = DEFAULT_BACKOFF_MAX_MS
): number {
  const exponential = baseMs * 2 ** attempt;
  const jitter = Math.random() * baseMs;
  return Math.min(exponential + jitter, maxMs);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ---------------------------------------------------------------------------
// fetchWithRetry – general-purpose retry wrapper
// ---------------------------------------------------------------------------
export interface RetryOptions {
  /** Maximum number of retry attempts (default 3). */
  maxRetries?: number;
  /** Base delay in ms for exponential backoff (default 500). */
  backoffBaseMs?: number;
  /** Maximum delay cap in ms (default 10 000). */
  backoffMaxMs?: number;
  /** Custom predicate – return true to retry, false to fail immediately. */
  shouldRetry?: (error: unknown) => boolean;
}

/**
 * Execute an async operation with automatic retry and exponential backoff.
 *
 * ```ts
 * const data = await fetchWithRetry(() => api.get('/stats/summary/'));
 * ```
 */
export async function fetchWithRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = DEFAULT_MAX_RETRIES,
    backoffBaseMs = DEFAULT_BACKOFF_BASE_MS,
    backoffMaxMs = DEFAULT_BACKOFF_MAX_MS,
    shouldRetry = isRetryable,
  } = options;

  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (attempt < maxRetries && shouldRetry(error)) {
        const delayMs = backoffMs(attempt, backoffBaseMs, backoffMaxMs);
        logger.warn(
          `[resilience] Attempt ${attempt + 1}/${maxRetries + 1} failed, retrying in ${Math.round(delayMs)}ms`
        );
        await sleep(delayMs);
      } else {
        break;
      }
    }
  }

  throw lastError;
}

// ---------------------------------------------------------------------------
// CircuitBreaker – client-side circuit breaker
// ---------------------------------------------------------------------------
type CircuitState = 'closed' | 'open' | 'half-open';

export interface CircuitBreakerOptions {
  /** Name for logging purposes. */
  name?: string;
  /** Consecutive failures before the circuit opens (default 5). */
  failureThreshold?: number;
  /** Milliseconds before the circuit transitions to half-open (default 60 000). */
  recoveryTimeoutMs?: number;
}

export class CircuitBreaker {
  readonly name: string;
  private readonly failureThreshold: number;
  private readonly recoveryTimeoutMs: number;

  private state: CircuitState = 'closed';
  private failureCount = 0;
  private lastFailureTime = 0;

  constructor(options: CircuitBreakerOptions = {}) {
    this.name = options.name ?? 'default';
    this.failureThreshold = options.failureThreshold ?? CIRCUIT_FAILURE_THRESHOLD;
    this.recoveryTimeoutMs = options.recoveryTimeoutMs ?? CIRCUIT_RECOVERY_TIMEOUT_MS;
  }

  /** Current state, accounting for recovery timeout. */
  getState(): CircuitState {
    if (this.state === 'open') {
      if (Date.now() - this.lastFailureTime >= this.recoveryTimeoutMs) {
        this.state = 'half-open';
        logger.info(`[circuit:${this.name}] Transitioned to half-open`);
      }
    }
    return this.state;
  }

  getFailureCount(): number {
    return this.failureCount;
  }

  /** Record a successful call – resets the breaker to closed. */
  recordSuccess(): void {
    if (this.failureCount > 0 || this.state === 'half-open') {
      logger.info(`[circuit:${this.name}] Closed after successful call`);
    }
    this.failureCount = 0;
    this.state = 'closed';
  }

  /** Record a failed call – may open the circuit. */
  recordFailure(): void {
    this.failureCount += 1;
    this.lastFailureTime = Date.now();
    if (this.failureCount >= this.failureThreshold) {
      if (this.state !== 'open') {
        logger.error(
          `[circuit:${this.name}] Opened after ${this.failureCount} consecutive failures`
        );
      }
      this.state = 'open';
    }
  }

  /** Reset to closed (useful in tests). */
  reset(): void {
    this.failureCount = 0;
    this.state = 'closed';
    this.lastFailureTime = 0;
  }

  /**
   * Execute an async function through the circuit breaker (with optional retry).
   *
   * ```ts
   * const cb = new CircuitBreaker({ name: 'api' });
   * const data = await cb.call(() => api.get('/endpoint'));
   * ```
   */
  async call<T>(fn: () => Promise<T>, retryOptions?: RetryOptions): Promise<T> {
    const currentState = this.getState();
    if (currentState === 'open') {
      logger.warn(`[circuit:${this.name}] Open – rejecting call`);
      throw new CircuitBreakerOpenError(this.name);
    }

    try {
      const result = retryOptions ? await fetchWithRetry(fn, retryOptions) : await fn();
      this.recordSuccess();
      return result;
    } catch (error) {
      this.recordFailure();
      throw error;
    }
  }
}

/** Error thrown when a call is made while the circuit is open. */
export class CircuitBreakerOpenError extends Error {
  constructor(circuitName: string) {
    super(`Circuit '${circuitName}' is open – call rejected`);
    this.name = 'CircuitBreakerOpenError';
  }
}
